import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { backendRequest } from '@/lib/backend/api';
import { useUserContext } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BusinessSLA from '@/_auth/forms/BusinessSLA';
import { logTrustScoreEvent } from '@/lib/appwrite/trustScore';
import { Lock, UserCheck, Shield, LogIn, UserPlus, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { buildSignInRedirect } from '@/lib/auth/redirect';

type VerificationStep = 'pan' | 'bank';
type VerificationStatus = {
  pan: boolean;
  bank: boolean;
};

export function KYCVerificationModal({
  userId,
  onComplete,
  onClose,
}: {
  userId: string;
  onComplete: (status: VerificationStatus) => void;
  onClose: () => void;
  isPublicView?: boolean;
}) {
  const { toast } = useToast();
  const { isAuthenticated } = useUserContext();
  const navigate = useNavigate();
  const [step, setStep] = useState<VerificationStep>('pan');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [status, setStatus] = useState<VerificationStatus>({
    pan: false,
    bank: false
  });

  // Form data
  const [panData, setPanData] = useState({ pan: '', name: '' });
  const [bankData, setBankData] = useState({ accountNumber: '', ifsc: '', name: '', phone: '' });

  // Check existing KYC status on load
  useEffect(() => {
    if (!isAuthenticated) {
      setCheckingStatus(false);
      return;
    }

    const checkExistingKYC = async () => {
      try {
        setCheckingStatus(true);
        const kycData = await backendRequest<any>(`/api/v1/kyc/${userId}/status`);

        if (kycData) {
          const newStatus = {
            pan: Boolean(kycData.panVerified),
            bank: Boolean(kycData.bankVerified),
          };
          setStatus(newStatus);

          // Set initial step based on what's already completed
          if (!newStatus.pan) {
            setStep('pan');
          } else if (!newStatus.bank) {
            setStep('bank');
          }
        }
      } catch (error) {
        console.error('Error checking existing KYC:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkExistingKYC();
  }, [userId, isAuthenticated]);

  const handleAuthRequired = () => {
    navigate(buildSignInRedirect(`${window.location.pathname}${window.location.search}`));
  };

  // Create Cashfree beneficiary after both verifications complete
  const createBeneficiary = async (): Promise<boolean> => {
    if (!userId) return false;

    try {
      console.log("Creating beneficiary for seller:", userId);
      const result = await backendRequest<any>("/api/v1/kyc/beneficiary", {
        method: "POST",
        body: JSON.stringify({ sellerId: userId }),
      });
      console.log("Beneficiary created successfully:", result);
      return true;
    } catch (error) {
      console.error("Beneficiary creation error:", error);
      return false;
    }
  };

  const verifyPan = async () => {
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    setLoading(true);
    try {
      const result = await backendRequest<any>("/api/v1/kyc/verify-pan", {
        method: "POST",
        body: JSON.stringify({
          userId,
          pan: panData.pan.toUpperCase(),
          name: panData.name
        }),
      });
      if (result.success ?? result.panVerified) {
        const newStatus = { ...status, pan: true };
        setStatus(newStatus);
        toast({ title: 'PAN Verified Successfully' });

        // Continue to bank verification or complete
        if (!newStatus.bank) {
          setStep('bank');
        } else {
          onComplete(newStatus);
          onClose();
        }
      } else {
        toast({
          title: 'PAN Verification Failed',
          description: result.message || 'Please check your details and try again',
          variant: 'destructive'
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Please enter a correct PAN number.';
      toast({
        title: 'PAN Verification Failed',
        description: /^PAN verification failed\.?$/i.test(message)
          ? 'Please enter a correct PAN number.'
          : message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyBankDetails = async () => {
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    setLoading(true);
    try {
      const result = await backendRequest<any>("/api/v1/kyc/verify-bank", {
        method: "POST",
        body: JSON.stringify({
          userId,
          accountNumber: bankData.accountNumber,
          ifsc: bankData.ifsc.toUpperCase(),
          name: bankData.name,
          phone: bankData.phone
        }),
      });
      if (result.success ?? result.bankVerified ?? result.isComplete) {
        const newStatus = { ...status, bank: true };
        setStatus(newStatus);

        // If both PAN and bank are verified, create beneficiary
        if (newStatus.pan) {
          toast({
            title: 'Verification Complete',
            description: 'Bank details verified. Setting up your payment account...'
          });

          // Create beneficiary in background
          const beneficiaryCreated = await createBeneficiary();

          if (beneficiaryCreated) {
            toast({
              title: 'KYC Complete',
              description: 'Your KYC verification is complete and your payment account is ready!'
            });
          } else {
            // Still complete KYC even if beneficiary creation fails
            // It can be retried later
            toast({
              title: 'KYC Verified',
              description: 'Your KYC is verified. Payment account setup will be completed shortly.'
            });
          }

          // Log trust score event
          logTrustScoreEvent(
            userId,
            'kyc_completed',
            50,
            'KYC verification completion (PAN + Bank)'
          );

          onComplete(newStatus);
          onClose();
        } else {
          toast({ title: 'Bank Details Verified Successfully' });
          onComplete(newStatus);
          onClose();
        }
      } else {
        toast({
          title: 'Bank Verification Failed',
          description: result.message || 'Please check your details and try again',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to verify bank details. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const canVerifyPan = panData.pan.length === 10 && panData.name.length > 2 && !loading;
  const canVerifyBank =
    bankData.accountNumber.length > 5 &&
    bankData.ifsc.length >= 11 &&
    bankData.name.length > 2 &&
    bankData.phone.length === 10 &&
    !loading;

  // Determine if all steps are completed
  const allVerified = status.pan && status.bank;

  // Public view - show preview of KYC process
  if (!isAuthenticated) {
    return (
      <div className="fixed text-gray-700 inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-600" />
              KYC Verification Preview
            </h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>

          {/* Preview content */}
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3">Verification Process</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <UserCheck className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium">PAN Card Verification</p>
                    <p className="text-sm text-gray-600">Verify your identity with PAN details</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-medium">Bank Account Verification</p>
                    <p className="text-sm text-gray-600">Secure payment processing setup</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-900 mb-2">Why verify?</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Receive online payments for your products and services</li>
                <li>• Bulk Deal with Secure escrow payment protection</li>
                <li>• Earn money on Bizz from Tips</li>
                <li>• Build trust with buyers</li>
                <li>• Access premium seller features</li>
              </ul>
            </div>

            {/* Authentication prompt */}
            <div className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-lg p-4 border border-primary-500/20">
              <div className="flex items-center gap-2 mb-3">
                <Lock size={16} className="text-primary-500" />
                <p className="font-medium text-gray-900">
                  Ready to get verified?
                </p>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Join BUSICONN to complete your KYC verification and start selling.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={handleAuthRequired}
                  className="flex-1"
                  size="sm"
                >
                  <LogIn className="mr-2" size={14} />
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/sign-up')}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <UserPlus className="mr-2" size={14} />
                  Join Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated user - show actual KYC form
  return (
    <div className="fixed text-gray-700 inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">KYC Verification</h3>
          <button
            onClick={() => {
              onComplete(status);
              onClose();
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        {/* Documents Required Info */}
        <div className="bg-gray-100 p-3 rounded-lg mb-4">
          <p className="font-semibold mb-2 text-sm">Documents Required</p>
          <p className="text-xs text-gray-700">• PAN Card (Identity Verification)</p>
          <p className="text-xs text-gray-700">• Bank Account (Payment Settlement)</p>
        </div>

        {/* Loading state while checking existing KYC */}
        {checkingStatus && (
          <div className="flex flex-col items-center justify-center py-10">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="mt-3 text-sm text-gray-500">Checking KYC status...</p>
          </div>
        )}

        {/* PAN Verification Step */}
        {!checkingStatus && step === 'pan' && (
          <div className="space-y-4">
            <h4 className="font-medium">PAN Card Verification</h4>

            {status.pan ? (
              // Already verified banner
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-700 mb-3">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">PAN Already Verified</span>
                </div>
                <Button
                  onClick={() => setStep('bank')}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Continue to Bank Verification
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Input
                  placeholder="PAN Number (e.g., ABCDE1234F)"
                  value={panData.pan}
                  onChange={(e) => setPanData({...panData, pan: e.target.value.toUpperCase()})}
                  maxLength={10}
                  disabled={loading}
                />
                <Input
                  placeholder="Full Name as on PAN"
                  value={panData.name}
                  onChange={(e) => setPanData({...panData, name: e.target.value})}
                  disabled={loading}
                />

                <p className="text-xs text-gray-500">
                  Enter your PAN number and full name exactly as shown on your PAN card.
                </p>

                <Button
                  onClick={verifyPan}
                  disabled={!canVerifyPan}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify PAN'
                  )}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Bank Verification Step */}
        {!checkingStatus && step === 'bank' && (
          <div className="space-y-4">
            <h4 className="font-medium">Bank Account Verification</h4>

            {status.bank ? (
              // Already verified banner
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-semibold">Bank Account Already Verified</span>
                </div>
              </div>
            ) : (
              <>
                <Input
                  placeholder="Account Number"
                  value={bankData.accountNumber}
                  onChange={(e) => setBankData({
                    ...bankData,
                    accountNumber: e.target.value.replace(/\D/g, '')
                  })}
                  disabled={loading}
                />
                <Input
                  placeholder="IFSC Code (e.g., SBIN0001234)"
                  value={bankData.ifsc}
                  onChange={(e) => setBankData({
                    ...bankData,
                    ifsc: e.target.value.toUpperCase()
                  })}
                  disabled={loading}
                />
                <Input
                  placeholder="Account Holder Name"
                  value={bankData.name}
                  onChange={(e) => setBankData({...bankData, name: e.target.value})}
                  disabled={loading}
                />
                <Input
                  placeholder="Registered Mobile Number"
                  value={bankData.phone}
                  onChange={(e) => setBankData({
                    ...bankData,
                    phone: e.target.value.replace(/\D/g, '').slice(0, 10)
                  })}
                  maxLength={10}
                  disabled={loading}
                />

                <p className="text-xs text-gray-500">
                  Enter details exactly as registered with your bank. We'll verify your account in real-time.
                </p>

                <p className="text-sm text-gray-600">
                  By continuing, you are agreeing to <BusinessSLA />
                </p>

                <Button
                  onClick={verifyBankDetails}
                  disabled={!canVerifyBank}
                  className="w-full"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Bank Details'
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setStep('pan')}
                  disabled={loading}
                  className="w-full"
                >
                  Back to PAN
                </Button>
              </>
            )}

            {/* Success state when all verified */}
            {status.pan && status.bank && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-green-800 text-center mb-2">
                  KYC Verification Complete!
                </h4>
                <p className="text-sm text-green-700 text-center mb-4">
                  Your identity has been successfully verified using PAN and Bank account details.
                </p>
                <Button
                  onClick={() => {
                    onComplete({ pan: true, bank: true });
                    onClose();
                  }}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Save & Continue Later button */}
        {!checkingStatus && !allVerified && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <Button
              variant="ghost"
              onClick={() => {
                onComplete(status);
                onClose();
              }}
              className="w-full text-gray-500"
            >
              Save & Continue Later
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
